/**
 * +ranger-specs: the Ranger's three specialisations (docs/design/classes/specs.md "Ranger"). Built
 * on the Warrior / Rogue / Mage reference files: every base key 1–7 evolves on the same key, the
 * signature mechanic lives in passives / auras, spec-only abilities sit on Q / E / R.
 *
 *  - Deadeye (ranged dps, a gunslinger): the Cylinder (6 rounds) is the whole bar; every shot costs a
 *    round and nothing comes back in combat but a reload. An empty cylinder reloads by itself; Roll &
 *    Reload (key 4) rolls back and reloads early. While Reloading, key 4 is Snap Shut: pressed inside
 *    the window (0.55–0.95 s, S8 `selfAuraElapsed`) it is a perfect reload (Hot Rounds: +25% for six
 *    shots); too early jams. Never mandatory: ignored, the reload just finishes at 1.2 s.
 *  - Grovewarden (healer, heals-over-time): Budding, Lifevine and the bloom trails layer HoTs; every
 *    kind on an ally is a stack of Thicket (+8% healing taken, the `tally` rule); Burstbloom eats one
 *    for an instant heal; Lifevine blooms when it ends or moves. Thorn Whip (Budding on an enemy)
 *    deals damage and buds the most injured ally: the solo engine.
 *  - Beastbound (ranged dps, an evolving spirit beast, S7 companions): a real beast unit fights at
 *    your side (a fox; a wolf at Rank II, level 20; a great stag at Ascension, level 30). Your hits
 *    and its hits on the same target within a second build Bond; Pack Fury spends it. Its own bar:
 *    Hunt / Stand Guard (E, one button that flips) and Fanged Maul (R).
 *
 * Display names live in RANGER_SPEC_NAME (one place: rename a spec there).
 * Names: original (checked against WoW's spell list; names.ts holds the denylist incl. the ones
 * avoided here). Numbers: tuned with `pnpm harness -- specs` (docs/design/classes/specs.md §8).
 */
import type { AbilityDef, AuraDef, Condition, EffectSpec, NpcDef, SpecDef } from '@keystone/sim';
import { ABILITY, BASE_ABILITY, BASE_AURA } from '../../ids';
import { OFF_GCD, SPELL_RANGE, aroundSelf, aroundTarget } from '../common';
import { type SpecInfo, infoOf } from './types';

const RA = BASE_ABILITY.ranger;
const AC = BASE_ABILITY.acolyte;
const L = ABILITY.lightbinder;

export const RANGER_SPEC = { deadeye: 'deadeye', grovewarden: 'grovewarden', beastbound: 'beastbound' } as const;

/**
 * The specs' player-facing names, in ONE place (the Trial cards, the sheet, character creation and
 * every SpecDef read them from here). "Deadeye" is also a WoW talent name: rename it here only.
 */
export const RANGER_SPEC_NAME = { deadeye: 'Deadeye', grovewarden: 'Grovewarden', beastbound: 'Beastbound' } as const;

// ============================================================================
// Ids
// ============================================================================

/** Ability ids (key in the comment). */
export const DEADEYE = {
  quickdraw: 'deadeye.quickdraw', // 1 (True Draw)
  fanTheHammer: 'deadeye.fan_the_hammer', // 2 (Longshot)
  flashbang: 'deadeye.flashbang', // 3 (Snare Coil)
  rollReload: 'deadeye.roll_reload', // 4 (Backflip)
  snapShut: 'deadeye.snap_shut', // 4 while Reloading
  ricochet: 'deadeye.ricochet', // 5 (Arrowfall)
  trailStride: 'deadeye.trail_stride', // 6 (Swiftfoot)
  disarmingShot: 'deadeye.disarming_shot', // 7 (Pin Shot)
  highNoon: 'deadeye.high_noon', // Q
} as const;
export const DEADEYE_AURA = {
  gunslinger: 'deadeye.gunslinger', // hidden passive: auto-reload on empty, Explosive Rounds, Quickdraw crit
  reloading: 'deadeye.reloading', // the reload (key 4 → Snap Shut); the HUD draws the window from it
  jammed: 'deadeye.jammed',
  hotRounds: 'deadeye.hot_rounds', // the perfect reload's reward
  explosiveRound: 'deadeye.explosive_round',
  rattled: 'deadeye.rattled', // Flashbang: your shots hit it harder
  flashStun: 'deadeye.flashbang_stun',
  highNoonMark: 'deadeye.high_noon_mark', // the volley lands when the mark ends (logged under this id)
  sprint: 'deadeye.trail_sprint',
  steadyHand: 'deadeye.steady_hand',
  strideSpent: 'deadeye.trail_stride_spent',
} as const;
/** The Cylinder (the whole bar). */
export const ROUNDS = 'rounds';
/** The reload, S8: a perfect Snap Shut between `perfectFromMs` and `perfectToMs` (the HUD's sweet spot). */
export const DEADEYE_RELOAD = { durationMs: 1200, perfectFromMs: 550, perfectToMs: 950, jamMs: 1400 } as const;

export const GROVEWARDEN = {
  budding: 'grovewarden.budding', // 1 (True Draw); Thorn Whip on an enemy
  burstbloom: 'grovewarden.burstbloom', // 2 (Longshot)
  snarlroot: 'grovewarden.snarlroot', // 3 (Snare Coil)
  wildstride: 'grovewarden.wildstride', // 4 (Backflip)
  overgrowthSurge: 'grovewarden.overgrowth_surge', // 5 (Arrowfall)
  groveStride: 'grovewarden.grove_stride', // 6 (Swiftfoot)
  thornpin: 'grovewarden.thornpin', // 7 (Pin Shot)
  lifevine: 'grovewarden.lifevine', // Q
  worldroot: 'grovewarden.worldroot', // E
} as const;
export const GROVEWARDEN_AURA = {
  verdance: 'grovewarden.verdance', // hidden passive: Thornpin buds the most injured ally
  thicket: 'grovewarden.thicket', // tally of the HoT kinds on an ally (+8% healing taken each)
  budding: 'grovewarden.budding_hot',
  lifevine: 'grovewarden.lifevine_hot',
  petalfall: 'grovewarden.petalfall', // Wildstride's trail
  quickening: 'grovewarden.quickening', // Grove Stride
  snarled: 'grovewarden.snarled',
  groveStride: 'grovewarden.grove_stride_speed',
  worldroot: 'grovewarden.worldroot_buff',
} as const;
/** Every Grovewarden heal-over-time (each tallies one Thicket stack). */
export const GROVEWARDEN_HOTS = [GROVEWARDEN_AURA.budding, GROVEWARDEN_AURA.lifevine, GROVEWARDEN_AURA.petalfall, GROVEWARDEN_AURA.quickening] as const;

export const BEASTBOUND = {
  markPrey: 'beastbound.mark_prey', // 1 (True Draw)
  packFury: 'beastbound.pack_fury', // 2 (Longshot)
  spiritSnare: 'beastbound.spirit_snare', // 3 (Snare Coil)
  boundLeap: 'beastbound.bound_leap', // 4 (Backflip)
  spiritHerd: 'beastbound.spirit_herd', // 5 (Arrowfall)
  packStride: 'beastbound.pack_stride', // 6 (Swiftfoot)
  howlShot: 'beastbound.howl_shot', // 7 (Pin Shot)
  spiritMerge: 'beastbound.spirit_merge', // Q
  guard: 'beastbound.guard', // E (the beast's bar): Guard while hunting…
  hunt: 'beastbound.hunt', // …E flips to Hunt while guarding
  maul: 'beastbound.maul', // R (the beast's bar)
  // The beast's own moves (companionCast: it acts in its own name).
  pounce: 'beastbound.pounce',
  furyBite: 'beastbound.fury_bite',
  spiritPin: 'beastbound.spirit_pin',
  interpose: 'beastbound.interpose',
  maulBite: 'beastbound.maul_bite',
  growl: 'beastbound.growl',
  howl: 'beastbound.howl',
  spiritRend: 'beastbound.spirit_rend',
} as const;
export const BEASTBOUND_AURA = {
  bond: 'beastbound.spirit_bond', // hidden passive: keeps the beast summoned (re-forms it)
  marked: 'beastbound.marked_prey', // your beast hits it 30% harder
  guarding: 'beastbound.guarding', // the owner's command (E flips to Hunt)
  guardStance: 'beastbound.guard_stance', // on the beast while guarding
  reforming: 'beastbound.reforming',
  merge: 'beastbound.spirit_merge_buff',
  pinned: 'beastbound.pinned',
  maulBleed: 'beastbound.maul_bleed',
  sprint: 'beastbound.pack_sprint',
  strideSpent: 'beastbound.pack_stride_spent',
  kindred: 'beastbound.kindred_spirit', // proc: the next Pack Fury costs half
} as const;
/** The spirit beast's forms (docs/design/classes/specs.md "Beastbound": evolution by level). */
export const BEAST_FORM = { fox: 'beastbound.spirit_fox', wolf: 'beastbound.spirit_wolf', stag: 'beastbound.great_stag' } as const;
/** Level each form arrives at: the fox with the spec (10), the wolf at Rank II (20), the stag at Ascension (30). */
export const BEAST_FORM_LEVEL = { fox: 10, wolf: 20, stag: 30 } as const;
export const BOND = 'bond';

// ============================================================================
// Helpers
// ============================================================================

const enemyShot = { targeting: 'enemy' as const, range: SPELL_RANGE, requiresFacing: true };
const allies = (radius: number, extra: object = {}) => ({ anchor: 'caster' as const, shape: { type: 'circle' as const, radius }, affects: 'allies' as const, ...extra });
const gainSelf = (resource: string, amount: number): EffectSpec => ({ type: 'resource', to: 'self', resource, amount });
const OUT_OF_COMBAT: Condition = { type: 'not', cond: { type: 'selfInCombat' } };
/** The base Swiftfoot toggle (out of combat), kept on key 6 by the Deadeye and the Beastbound. */
const swiftfootToggle: EffectSpec = {
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: BASE_AURA.swiftfoot },
  then: [{ type: 'removeAura', to: 'self', auraId: BASE_AURA.swiftfoot }],
  else: [{ type: 'applyAura', to: 'self', aura: { id: BASE_AURA.swiftfoot, name: 'Swiftfoot', kind: 'buff', durationMs: 0, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: 0.3 }], flags: { removeInCombat: true } } }],
};

// ============================================================================
// Deadeye (ranged dps): the Cylinder, the perfect reload, Hammer Spray
// ============================================================================

const DE = DEADEYE;
const DEA = DEADEYE_AURA;
const hasRounds = (n: number): Condition => ({ type: 'resourceAtLeast', resource: ROUNDS, amount: n });
/** Shots that read Rattled (Flashbang) and Hot Rounds. */
const rattledBonus = { auraId: DEA.rattled, on: 'target' as const, mult: 0.15 };

/** Hammer Spray's bonus per Hot Round left in the cylinder. */
const HOT_SPRAY_PER_ROUND = 0.06;
const hotRounds: AuraDef = {
  id: DEA.hotRounds,
  name: 'Hot Rounds',
  kind: 'buff',
  durationMs: 20_000,
  stacking: 'stack',
  maxStacks: 6,
  mods: [
    { stat: 'damageDonePct', value: 0.25, ignoreStacks: true, abilityIds: [DE.quickdraw, DE.ricochet, DEA.highNoonMark] },
    // +specs balance: Hammer Spray fires every Hot Round left at once: +6% per round (+36% from a fresh
    // perfect reload, cooling with every Quickdraw taken first). It used to be a flat +25% at any count,
    // so spraying the last hot round was as good as spraying six.
    { stat: 'damageDonePct', value: HOT_SPRAY_PER_ROUND, abilityIds: [DE.fanTheHammer] },
  ],
  // One round per shot; Hammer Spray spends the rest (its effects).
  procs: [{ on: 'castResolved', abilityIds: [DE.quickdraw, DE.ricochet], consumeStack: true, effects: [] }],
  ui: { proc: true, stackCounterOn: [DE.quickdraw] },
};

const explosiveRound: AuraDef = {
  id: DEA.explosiveRound,
  name: 'Explosive Round',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'refresh',
  procs: [{ on: 'castResolved', abilityIds: [DE.quickdraw, DE.ricochet], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [DE.quickdraw] } },
};

const reloading: AuraDef = {
  id: DEA.reloading,
  name: 'Reloading',
  kind: 'buff',
  durationMs: DEADEYE_RELOAD.durationMs,
  stacking: 'refresh',
  // Key 4 is Snap Shut while the reload runs (a perfect window inside it).
  overrides: [{ abilityId: DE.rollReload, with: DE.snapShut }],
  // Ignored, the reload just finishes.
  procs: [{ on: 'auraExpire', effects: [gainSelf(ROUNDS, 6)] }],
  ui: { glow: { abilityIds: [DE.rollReload] } },
};

const jammed: AuraDef = {
  id: DEA.jammed,
  name: 'Jammed',
  kind: 'debuff',
  durationMs: DEADEYE_RELOAD.jamMs,
  stacking: 'refresh',
  procs: [{ on: 'auraExpire', effects: [gainSelf(ROUNDS, 6)] }],
};

const rattled: AuraDef = { id: DEA.rattled, name: 'Rattled', kind: 'debuff', durationMs: 10_000, stacking: 'refresh' };
const flashStun: AuraDef = { id: DEA.flashStun, name: 'Flash-Blind', kind: 'debuff', durationMs: 2000, stacking: 'refresh', cc: 'stun' };

const highNoonMark: AuraDef = {
  id: DEA.highNoonMark,
  name: 'High Noon',
  kind: 'debuff',
  durationMs: 3000,
  stacking: 'refresh',
  // When the clock strikes: one shot at every mark at once (on any removal; a dead mark takes nothing).
  onRemove: [{ type: 'damage', school: 'physical', amount: { coef: 5.0, variance: 0.05, perStack: rattledBonus }, critBonus: 0.25 }],
};

const steadyHand: AuraDef = {
  id: DEA.steadyHand,
  name: 'Cool Hand',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [{ stat: 'critChanceBonus', value: 1, abilityIds: [DE.quickdraw] }],
  procs: [{ on: 'castResolved', abilityIds: [DE.quickdraw], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [DE.quickdraw] } },
};
const trailSprint: AuraDef = { id: DEA.sprint, name: 'Trail Stride', kind: 'buff', durationMs: 4000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: 0.4 }] };
const strideSpent: AuraDef = { id: DEA.strideSpent, name: 'Trail Stride', kind: 'debuff', durationMs: 30_000, stacking: 'refresh', flags: { hidden: true } };

/** Hidden passive: the cylinder reloads by itself when it runs dry; Quickdraw crits load an Explosive Round. */
const gunslinger: AuraDef = {
  id: DEA.gunslinger,
  name: 'Gunslinger',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  mods: [{ stat: 'critChanceBonus', value: 0.15, abilityIds: [DE.quickdraw] }],
  procs: [
    {
      on: 'castResolved',
      if: { type: 'and', all: [{ type: 'not', cond: hasRounds(1) }, { type: 'selfLacksAura', auraId: DEA.reloading }, { type: 'selfLacksAura', auraId: DEA.jammed }] },
      effects: [{ type: 'applyAura', to: 'self', aura: reloading }],
    },
    { on: 'critDealt', abilityIds: [DE.quickdraw], effects: [{ type: 'applyAura', to: 'self', aura: explosiveRound }] },
    // A landed Disarming Shot slots two rounds back in.
    { on: 'interruptDealt', abilityIds: [DE.disarmingShot], effects: [gainSelf(ROUNDS, 2)] },
  ],
};

/** An Explosive Round, if loaded: the shot also bursts 4 yd around the target. */
const explosive: EffectSpec = {
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: DEA.explosiveRound },
  then: [{ type: 'damage', school: 'fire', to: 'area', area: { ...aroundTarget(4, 8), excludePrimary: true }, amount: { coef: 1.6, variance: 0.05 }, isAoE: true }],
};

const quickdraw: AbilityDef = {
  id: DE.quickdraw,
  name: 'Quickdraw',
  school: 'physical',
  description: 'Fire one round from the hip: fast and accurate. Usable on the move. Crits load an Explosive Round.',
  ...enemyShot,
  gcdMs: 1300,
  cost: [{ resource: ROUNDS, amount: 1 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 3.05, variance: 0.05, perStack: rattledBonus } }, explosive],
  ui: {
    tags: ['builder'],
    color: '#e6b46a',
    animAs: RA.trueDraw,
    desc: 'Fire one round from the hip for {damage} Physical damage. Usable on the move, 1.3 sec global cooldown, +15% crit chance. A crit loads an Explosive Round: your next shot also bursts for {damage2} Fire damage around the target. Costs 1 round; an empty cylinder reloads itself.',
    hint: 'Press 1 to shoot. Six rounds, then the reload: press 4 inside the window for Hot Rounds.',
  },
};

const fanTheHammer: AbilityDef = {
  id: DE.fanTheHammer,
  name: 'Hammer Spray',
  school: 'physical',
  description: 'Empty every round left in the cylinder (2 at least) in one blur: a spray into the target and a cone behind it. More rounds, more damage. Every Hot Round left fires with it: +6% each.',
  ...enemyShot,
  range: 32,
  cooldown: { durationMs: 10_000 },
  spendAll: { resource: ROUNDS, min: 2 },
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 0.2, variance: 0.05, perSpent: 5.0, perStack: rattledBonus } },
    { type: 'damage', school: 'physical', to: 'area', area: { anchor: 'caster', shape: { type: 'cone', range: 34, angle: Math.PI / 3 }, affects: 'enemies', maxTargets: 6, excludePrimary: true }, amount: { coef: 0.1, variance: 0.05, perSpent: 5.0 }, isAoE: true },
    { type: 'removeAura', to: 'self', auraId: DEA.hotRounds },
  ],
  ui: {
    tags: ['spender', 'aoe'],
    color: '#ffcf7a',
    animAs: RA.longshot,
    desc: 'Empty the cylinder (at least 2 rounds) in one blur: {damage} Physical damage to the target and {damage2} to up to 6 enemies in a cone toward it, both growing by 500% of the base for every round fired. Every Hot Round left fires with it: +6% damage each (+36% from a fresh perfect reload). 10 sec cooldown.',
    hint: 'Press 2 right after a perfect reload: every Hot Round fires with it. Each Quickdraw first cools the spray.',
  },
};

const flashbang: AbilityDef = {
  id: DE.flashbang,
  name: 'Flashbang',
  school: 'fire',
  description: 'Toss a flash charge at the target: enemies within 6 yd are stunned for 2 s and Rattled for 10 s (your shots hit them 15% harder).',
  ...enemyShot,
  range: 30,
  cooldown: { durationMs: 20_000 },
  effects: [
    { type: 'damage', school: 'fire', to: 'area', area: aroundTarget(6, 10), amount: { coef: 1.4, variance: 0.05 }, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundTarget(6, 10), aura: flashStun },
    { type: 'applyAura', to: 'area', area: aroundTarget(6, 10), aura: rattled },
  ],
  ui: {
    tags: ['cooldown', 'aoe'],
    color: '#fff1c2',
    animAs: RA.snareCoil,
    desc: 'Toss a flash charge at the target: every enemy within 6 yd takes {damage} Fire damage, is stunned for 2 sec and Rattled for 10 sec: your shots hit Rattled enemies 15% harder. 20 sec cooldown.',
    hint: 'Press 3 on cooldown: a stun, and your shots hit harder for 10 s.',
  },
};

const rollReload: AbilityDef = {
  id: DE.rollReload,
  name: 'Roll & Reload',
  school: 'physical',
  description: 'Roll 10 yd back and start reloading. While the reload runs this key is Snap Shut: press it inside the window for a perfect reload.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 8000 },
  requiresMobility: true,
  usableWhileCasting: true,
  usableIf: { type: 'selfLacksAura', auraId: DEA.jammed },
  effects: [
    { type: 'dash', mode: 'backward', distance: 10, speed: 32 },
    { type: 'conditional', cond: { type: 'not', cond: hasRounds(6) }, then: [{ type: 'applyAura', to: 'self', aura: reloading }] },
  ],
  ui: {
    tags: ['defensive', 'movement'],
    color: '#d9b98a',
    animAs: RA.backflip,
    desc: 'Roll 10 yd back (still facing your target) and start a 1.2 sec reload. While it runs this key becomes Snap Shut. Off the global cooldown, 8 sec cooldown.',
    hint: 'Press 4 to roll out and reload early, then 4 again inside the gold window.',
  },
};

const perfectReload: Condition = { type: 'selfAuraElapsed', auraId: DEA.reloading, minMs: DEADEYE_RELOAD.perfectFromMs, maxMs: DEADEYE_RELOAD.perfectToMs };
const earlyReload: Condition = { type: 'selfAuraElapsed', auraId: DEA.reloading, maxMs: DEADEYE_RELOAD.perfectFromMs - 1 };

const snapShut: AbilityDef = {
  id: DE.snapShut,
  name: 'Snap Shut',
  school: 'physical',
  description: 'Slam the cylinder shut. Inside the window (0.55–0.95 s into the reload) it is a perfect reload: 6 rounds and Hot Rounds (+25% damage for 6 shots). Too early jams the gun.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  usableWhileCasting: true,
  usableIf: { type: 'selfHasAura', auraId: DEA.reloading },
  effects: [
    {
      type: 'conditional',
      cond: perfectReload,
      then: [{ type: 'removeAura', to: 'self', auraId: DEA.reloading }, gainSelf(ROUNDS, 6), { type: 'applyAura', to: 'self', aura: hotRounds, stacks: 6 }],
      else: [
        {
          type: 'conditional',
          cond: earlyReload,
          then: [{ type: 'removeAura', to: 'self', auraId: DEA.reloading }, { type: 'applyAura', to: 'self', aura: jammed }],
          else: [{ type: 'removeAura', to: 'self', auraId: DEA.reloading }, gainSelf(ROUNDS, 6)],
        },
      ],
    },
  ],
  ui: {
    tags: ['cooldown'],
    color: '#ffd76a',
    animAs: RA.longshot,
    desc: 'Slam the cylinder shut. Between 0.55 and 0.95 sec into the reload: a perfect reload, 6 rounds and Hot Rounds (+25% damage on your next 6 shots). Earlier jams the gun (the reload ends 1.4 sec later). Later just finishes the reload. Off the global cooldown.',
    hint: 'Press 4 again while the reload bar crosses the gold mark.',
  },
};

const ricochet: AbilityDef = {
  id: DE.ricochet,
  name: 'Ricochet',
  school: 'physical',
  description: 'A trick shot that hits the target hard, then ricochets into up to 4 more enemies within 10 yd.',
  ...enemyShot,
  cooldown: { durationMs: 6000 },
  cost: [{ resource: ROUNDS, amount: 1 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 3.75, variance: 0.05, perStack: rattledBonus } },
    { type: 'damage', school: 'physical', to: 'area', area: { ...aroundTarget(10, 4), excludePrimary: true }, amount: { coef: 1.3, variance: 0.05 }, isAoE: true },
    explosive,
  ],
  ui: {
    tags: ['aoe'],
    color: '#f0c070',
    animAs: RA.arrowfall,
    desc: 'A trick shot: {damage} Physical damage to the target, then it ricochets into up to 4 more enemies within 10 yd for {damage2}. Costs 1 round, 6 sec cooldown. Uses a loaded Explosive Round.',
    hint: 'Press 5 on cooldown: your hardest single round, and it bounces through a pack.',
  },
};

const trailStride: AbilityDef = {
  id: DE.trailStride,
  name: 'Trail Stride',
  school: 'nature',
  description: 'Out of combat: run 30% faster (toggle). In combat: a 4 s sprint, and your next Quickdraw is a sure crit (30 s).',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 1000 },
  usableIf: { type: 'or', any: [OUT_OF_COMBAT, { type: 'selfHasAura', auraId: BASE_AURA.swiftfoot }, { type: 'selfLacksAura', auraId: DEA.strideSpent }] },
  effects: [
    {
      type: 'conditional',
      cond: { type: 'selfInCombat' },
      then: [{ type: 'applyAura', to: 'self', aura: trailSprint }, { type: 'applyAura', to: 'self', aura: steadyHand }, { type: 'applyAura', to: 'self', aura: strideSpent }],
      else: [swiftfootToggle],
    },
  ],
  ui: {
    tags: ['movement'],
    color: '#c9d68a',
    animAs: RA.swiftfoot,
    desc: 'Out of combat: toggle, run 30% faster until you press it again or a fight starts. In combat: sprint (+40% speed for 4 sec) and your next Quickdraw is a sure crit (an Explosive Round). Once every 30 sec in combat.',
    hint: 'Press 6 between fights to run, or mid-fight to reposition and load a sure crit.',
  },
};

const disarmingShot: AbilityDef = {
  id: DE.disarmingShot,
  name: 'Disarming Shot',
  school: 'physical',
  description: "Shoot the weapon out of a caster's hand: interrupts its spell and locks that school for 3 s. Costs no round; a landed interrupt slots 2 rounds back in.",
  ...enemyShot,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6 } }, { type: 'interrupt', lockoutMs: 3000 }],
  ui: { tags: ['interrupt'], color: '#e0b25a', animAs: RA.pinShot, desc: "Shoot the weapon out of a caster's hand for {damage} Physical damage: interrupts its spell and locks that school for 3 sec. Costs no round; a landed interrupt slots 2 rounds back in. Off the global cooldown.", hint: 'Press 7 while an enemy casts: a free interrupt that reloads 2 rounds.' },
};

const highNoon: AbilityDef = {
  id: DE.highNoon,
  name: 'High Noon',
  school: 'physical',
  description: 'Mark up to 6 enemies within 15 yd of the target. 3 s later you fire on every mark at once.',
  ...enemyShot,
  cooldown: { durationMs: 60_000 },
  effects: [{ type: 'applyAura', to: 'area', area: aroundTarget(15, 6), aura: highNoonMark }],
  ui: {
    tags: ['cooldown', 'aoe'],
    color: '#ffb347',
    animAs: L.dawnbringer,
    desc: 'Mark the target and up to 5 more enemies within 15 yd. When the clock strikes 3 sec later you fire on every mark at once: {damage} Physical damage each (+25% crit chance). 60 sec cooldown.',
    hint: 'Press Q on a pack (or a boss): three seconds later every mark gets a bullet.',
  },
};

export const deadeye: SpecDef = {
  id: RANGER_SPEC.deadeye,
  baseClassId: 'ranger',
  name: RANGER_SPEC_NAME.deadeye,
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [RA.trueDraw]: DE.quickdraw,
    [RA.longshot]: DE.fanTheHammer,
    [RA.snareCoil]: DE.flashbang,
    [RA.backflip]: DE.rollReload,
    [RA.arrowfall]: DE.ricochet,
    [RA.swiftfoot]: DE.trailStride,
    [RA.pinShot]: DE.disarmingShot,
  },
  extraAbilities: [{ key: 'Q', abilityId: DE.highNoon }],
  abilities: [quickdraw, fanTheHammer, flashbang, rollReload, snapShut, ricochet, trailStride, disarmingShot, highNoon],
  // The Cylinder is the whole bar: 6 rounds, nothing comes back in combat but a reload; out of combat it tops itself up.
  resources: [{ id: ROUNDS, name: 'Cylinder', max: 6, start: 6, regenPerSec: 0, regenPerSecOoc: 3 }],
  passives: [gunslinger],
  auras: [reloading, jammed, hotRounds, explosiveRound, rattled, flashStun, highNoonMark, steadyHand, trailSprint, strideSpent],
  // Auto Shot becomes a pistol shot (same timing as the bow).
  weapon: { kind: 'ranged', damage: { coef: 1.9, variance: 0.05 }, speedMs: 3000, school: 'physical', range: 40 },
  color: '#c99a5b',
  ui: {
    tagline: 'A gunslinger. Six shots, a perfect reload and a duel at noon.',
    fantasy: 'Swap the bow for a long-barrelled flintlock revolver. Six shots per cylinder; slam the cylinder shut at just the right moment and the next six shots burn hotter.',
    signature: 'The Cylinder: 6 rounds, then a 1.2 s reload; Snap Shut (key 4) inside the gold window is a perfect reload (+25% damage for six shots). Hammer Spray empties the cylinder in one blur.',
    weapon: 'revolver',
    tint: '#c99a5b',
    glow: '#ffe2a8',
  },
};

// ============================================================================
// Grovewarden (healer): heals-over-time, Thicket, Burstbloom, Lifevine
// ============================================================================

const GW = GROVEWARDEN;
const GWA = GROVEWARDEN_AURA;
const MANA = 'mana';

/** Thicket: one stack per HoT kind on the ally (combat/tally.ts), +8% healing taken each (from everyone). */
const thicket: AuraDef = {
  id: GWA.thicket,
  name: 'Thicket',
  kind: 'buff',
  durationMs: 0,
  stacking: 'stack',
  maxStacks: 4,
  mods: [{ stat: 'healingTakenPct', value: 0.08 }],
  ui: { stackCounterOn: [GW.burstbloom] },
};

const hot = (id: string, name: string, durationMs: number, intervalMs: number, coef: number, extra: Partial<AuraDef> = {}): AuraDef => ({
  id,
  name,
  kind: 'buff',
  durationMs,
  stacking: 'refresh',
  pandemic: true,
  tally: GWA.thicket,
  periodic: { intervalMs, hasteScaled: true, effects: [{ type: 'heal', amount: { coef, variance: 0.05 } }] },
  ...extra,
});

const buddingHot = hot(GWA.budding, 'Budding', 15_000, 1500, 0.5);
const lifevineHot = hot(GWA.lifevine, 'Lifevine', 12_000, 1000, 0.6, {
  // It blooms when it ends, moves or is eaten (a dead holder takes nothing).
  onRemove: [{ type: 'heal', amount: { coef: 3.0, variance: 0.05 } }],
});
const petalfall = hot(GWA.petalfall, 'Petalfall', 3000, 1000, 0.32);
const quickening = hot(GWA.quickening, 'Greenstep', 8000, 1000, 0.4);

/** Budding on the most injured ally within 40 yd of the caster (Thorn Whip, Thornpin). */
const budMostInjured: EffectSpec = { type: 'applyAura', to: 'area', area: allies(40, { maxTargets: 1, lowestHp: true }), aura: buddingHot };

const verdance: AuraDef = {
  id: GWA.verdance,
  name: 'Verdance',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [{ on: 'interruptDealt', abilityIds: [GW.thornpin], effects: [budMostInjured] }],
};

const budding: AbilityDef = {
  id: GW.budding,
  name: 'Budding',
  school: 'nature',
  description: 'On an ally: a heal over time for 15 s. On an enemy it is Thorn Whip: nature damage, and a Budding sprouts on your most injured ally.',
  targeting: 'any',
  range: SPELL_RANGE,
  cost: [{ resource: MANA, amount: 160 }],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'targetIsEnemy' },
      then: [{ type: 'damage', school: 'nature', amount: { coef: 4.4, variance: 0.05 } }, budMostInjured],
      else: [{ type: 'applyAura', aura: buddingHot }],
    },
  ],
  ui: {
    tags: ['heal', 'builder'],
    color: '#8fd16a',
    animAs: AC.tend,
    desc: 'On an ally (or yourself): Budding heals {heal} every 1.5 sec for 15 sec, and counts as a heal-over-time for Thicket. On an enemy it is Thorn Whip: {damage} Nature damage, and a Budding sprouts on your most injured ally within 40 yd. Instant.',
    hint: 'Press 1 on allies to keep Budding rolling; on an enemy it whips and still heals.',
  },
};

const hasHot = (id: string): Condition => ({ type: 'targetHasAura', auraId: id, fromSelf: true });

const burstbloom: AbilityDef = {
  id: GW.burstbloom,
  name: 'Burstbloom',
  school: 'nature',
  description: 'Force one of your heals-over-time on an ally to bloom at once: a big instant heal. Eats Lifevine first (it blooms too), then Budding, then a trail HoT.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cost: [{ resource: MANA, amount: 220 }],
  cooldown: { durationMs: 12_000, charges: 2 },
  usableIf: { type: 'or', any: GROVEWARDEN_HOTS.map(hasHot) },
  effects: [
    { type: 'heal', amount: { coef: 6.0, variance: 0.05 } },
    {
      type: 'conditional',
      cond: hasHot(GWA.lifevine),
      then: [{ type: 'removeAura', auraId: GWA.lifevine }],
      else: [
        {
          type: 'conditional',
          cond: hasHot(GWA.budding),
          then: [{ type: 'removeAura', auraId: GWA.budding }],
          else: [{ type: 'conditional', cond: hasHot(GWA.quickening), then: [{ type: 'removeAura', auraId: GWA.quickening }], else: [{ type: 'removeAura', auraId: GWA.petalfall }] }],
        },
      ],
    },
  ],
  ui: {
    tags: ['heal', 'spender'],
    color: '#d8f5a0',
    animAs: L.holyShock,
    desc: 'Make one of your heals-over-time on an ally bloom at once: an instant {heal} heal. Eats Lifevine first (which blooms as well), else Budding, else a trail HoT. 2 charges, 12 sec recharge.',
    hint: 'Press 2 on an ally with your HoTs when a spike lands: an instant big heal.',
  },
};

const snarled: AuraDef = { id: GWA.snarled, name: 'Snarlroot', kind: 'debuff', durationMs: 4000, stacking: 'refresh', cc: 'root' };

const snarlroot: AbilityDef = {
  id: GW.snarlroot,
  name: 'Snarlroot',
  school: 'nature',
  description: 'Roots burst from the ground around you: enemies within 8 yd are rooted for 4 s. The roots feed you mana.',
  targeting: 'none',
  cooldown: { durationMs: 20_000 },
  effects: [
    { type: 'damage', school: 'nature', to: 'area', area: aroundSelf(8, 10), amount: { coef: 0.8, variance: 0.05 }, isAoE: true },
    { type: 'applyAura', to: 'area', area: aroundSelf(8, 10), aura: snarled },
    { type: 'conditional', cond: { type: 'enemiesNearSelf', radius: 8, atLeast: 1 }, then: [gainSelf(MANA, 400)] },
  ],
  ui: { tags: ['cooldown', 'aoe'], color: '#7fb069', animAs: RA.snareCoil, desc: 'Roots burst from the ground: every enemy within 8 yd takes {damage} Nature damage and is rooted for 4 sec. With an enemy caught, you regain 400 mana. 20 sec cooldown.', hint: 'Press 3 when enemies reach you: they are stuck, and you get mana back.' },
};

const wildstride: AbilityDef = {
  id: GW.wildstride,
  name: 'Wildstride',
  school: 'nature',
  description: 'Leap 15 yd back. Where you stood a ring of falling petals heals allies inside for 6 s (Petalfall, a heal-over-time).',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 20_000 },
  requiresMobility: true,
  usableWhileCasting: true,
  effects: [
    {
      type: 'zone',
      zoneId: GW.wildstride,
      anchor: 'caster',
      shape: { type: 'circle', radius: 6 },
      durationMs: 6000,
      affects: 'allies',
      uniquePerCaster: true,
      tick: { intervalMs: 1000, effects: [{ type: 'applyAura', aura: petalfall }] },
    },
    { type: 'dash', mode: 'backward', distance: 15, speed: 36 },
  ],
  ui: { tags: ['movement', 'heal'], color: '#f5c6d8', animAs: RA.backflip, desc: 'Leap 15 yd back. Where you stood, petals fall for 6 sec: allies inside keep Petalfall, healing {heal} a second (a heal-over-time). Off the global cooldown.', hint: 'Press 4 to escape: the petals you leave behind heal whoever stands in them.' },
};

const overgrowthSurge: AbilityDef = {
  id: GW.overgrowthSurge,
  name: 'Overgrowth Surge',
  school: 'nature',
  description: 'Growth surges through the party: the 6 most injured allies within 30 yd are healed and get Budding and Greenstep.',
  targeting: 'none',
  cost: [{ resource: MANA, amount: 600 }],
  cooldown: { durationMs: 20_000 },
  effects: [
    { type: 'heal', to: 'area', area: allies(30), smart: 6, amount: { coef: 1.6, variance: 0.05 } },
    { type: 'applyAura', to: 'area', area: allies(30, { maxTargets: 6, lowestHp: true }), aura: buddingHot },
    { type: 'applyAura', to: 'area', area: allies(30, { maxTargets: 6, lowestHp: true }), aura: quickening },
  ],
  ui: { tags: ['groupHeal', 'heal', 'cooldown'], color: '#a6e07a', animAs: AC.sanctumCircle, desc: 'Growth surges through the party: the 6 most injured allies within 30 yd are healed for {heal} and get Budding and Greenstep (two heals-over-time). 20 sec cooldown.', hint: 'Press 5 before or after group damage: Budding on everyone at once.' },
};

const groveStrideSpeed: AuraDef = { id: GWA.groveStride, name: 'Grove Stride', kind: 'buff', durationMs: 4000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: 0.3 }] };

const groveStride: AbilityDef = {
  id: GW.groveStride,
  name: 'Grove Stride',
  school: 'nature',
  description: 'You and allies within 30 yd run 30% faster for 4 s and gain Greenstep (a heal-over-time). Works in combat too.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 25_000 },
  effects: [
    { type: 'applyAura', to: 'area', area: allies(30), aura: groveStrideSpeed },
    { type: 'applyAura', to: 'area', area: allies(30), aura: quickening },
  ],
  ui: { tags: ['movement', 'heal'], color: '#b8e08a', animAs: RA.swiftfoot, desc: 'You and every ally within 30 yd run 30% faster for 4 sec and gain Greenstep: {heal} healing a second for 8 sec (a heal-over-time). Off the global cooldown, 25 sec cooldown.', hint: 'Press 6 to move the group, or just for one more HoT on everyone close.' },
};

const thornpin: AbilityDef = {
  id: GW.thornpin,
  name: 'Thornpin',
  school: 'nature',
  description: 'A thorn through the caster’s hand: interrupts its spell and locks that school for 3 s. A landed interrupt sprouts Budding on your most injured ally.',
  ...enemyShot,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  effects: [{ type: 'damage', school: 'nature', amount: { coef: 0.5 } }, { type: 'interrupt', lockoutMs: 3000 }],
  ui: { tags: ['interrupt'], color: '#9cc76a', animAs: RA.pinShot, desc: 'A thorn through the caster’s hand for {damage} Nature damage: interrupts its spell and locks that school for 3 sec. A landed interrupt sprouts Budding on your most injured ally. Off the global cooldown.', hint: 'Press 7 while an enemy casts.' },
};

const lifevine: AbilityDef = {
  id: GW.lifevine,
  name: 'Lifevine',
  school: 'nature',
  description: 'A strong vine wraps one ally (one at a time): heals every second for 12 s, then blooms for a big heal when it ends, moves or is refreshed.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cost: [{ resource: MANA, amount: 260 }],
  effects: [
    // One Lifevine at a time: the old one blooms where it was (and a refresh blooms in place).
    { type: 'removeAura', to: 'area', area: allies(60), auraId: GWA.lifevine },
    { type: 'applyAura', aura: lifevineHot },
  ],
  ui: { tags: ['heal'], color: '#6fcf6f', animAs: L.flashHeal, desc: 'Wrap one ally in Lifevine: {heal} healing every second for 12 sec (a heal-over-time), then it blooms for {heal2} when it ends, is refreshed, moves to another ally or is eaten by Burstbloom. One Lifevine at a time.', hint: 'Press Q to keep Lifevine on the tank: refresh it to bloom it early.' },
};

const worldrootBuff: AuraDef = {
  id: GWA.worldroot,
  name: 'Worldroot',
  kind: 'buff',
  durationMs: 10_000,
  stacking: 'refresh',
  mods: [
    { stat: 'healingDonePct', value: 0.5 },
    { stat: 'resourceCostPct', value: -1, abilityIds: [GW.budding] },
  ],
  ui: { proc: true },
};

const worldroot: AbilityDef = {
  id: GW.worldroot,
  name: 'Worldroot',
  school: 'nature',
  description: 'Draw on the roots of the world for 10 s: +50% healing, Budding costs nothing, and Overgrowth Surge is ready again.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90_000 },
  effects: [{ type: 'applyAura', to: 'self', aura: worldrootBuff }, { type: 'modifyCooldown', abilityId: GW.overgrowthSurge, reset: true }],
  ui: { tags: ['cooldown', 'heal'], color: '#4fae5f', animAs: L.dawnbringer, desc: 'For 10 sec your healing is 50% stronger (heals-over-time you apply keep it for their whole duration), Budding costs no mana, and Overgrowth Surge is ready again. Off the global cooldown, 90 sec cooldown.', hint: 'Press E before heavy damage, then roll Budding on everyone.' },
};

export const grovewarden: SpecDef = {
  id: RANGER_SPEC.grovewarden,
  baseClassId: 'ranger',
  name: RANGER_SPEC_NAME.grovewarden,
  role: 'healer',
  unlockLevel: 10,
  evolutions: {
    [RA.trueDraw]: GW.budding,
    [RA.longshot]: GW.burstbloom,
    [RA.snareCoil]: GW.snarlroot,
    [RA.backflip]: GW.wildstride,
    [RA.arrowfall]: GW.overgrowthSurge,
    [RA.swiftfoot]: GW.groveStride,
    [RA.pinShot]: GW.thornpin,
  },
  extraAbilities: [
    { key: 'Q', abilityId: GW.lifevine },
    { key: 'E', abilityId: GW.worldroot },
  ],
  abilities: [budding, burstbloom, snarlroot, wildstride, overgrowthSurge, groveStride, thornpin, lifevine, worldroot],
  // Mana replaces Aim (the Acolyte's pool and regen).
  resources: [{ id: MANA, name: 'Mana', max: 10_000, start: 10_000, regenPerSec: 95, regenPerSecOoc: 600 }],
  passives: [verdance],
  auras: [thicket, buddingHot, lifevineHot, petalfall, quickening, snarled, groveStrideSpeed, worldrootBuff],
  color: '#5fae5f',
  ui: {
    tagline: 'A heal-over-time healer who blankets the party in blooming growth.',
    fantasy: 'You heal by layering living magic onto allies: buds, vines, blossoms. The more growth on someone, the harder it all heals. Plan ahead, roll your heals over time, and let the garden work.',
    signature: 'Thicket: each kind of your heals-over-time on an ally adds a stack (up to 4), +8% healing taken each; Burstbloom eats one for an instant heal; Lifevine blooms when it ends.',
    weapon: 'bow',
    tint: '#5fae5f',
    glow: '#c4f5a8',
  },
};

// ============================================================================
// Beastbound (ranged dps): the spirit beast (S7 companion), Bond, Pack Fury
// ============================================================================

const BB = BEASTBOUND;
const BBA = BEASTBOUND_AURA;

const marked: AuraDef = { id: BBA.marked, name: 'Marked Prey', kind: 'debuff', durationMs: 12_000, stacking: 'refresh' };
const guarding: AuraDef = {
  id: BBA.guarding,
  name: 'Guarding',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { persistThroughDeath: true },
  // The beast's bar: E is Guard while it hunts, Hunt while it guards.
  overrides: [{ abilityId: BB.guard, with: BB.hunt }],
};
const guardStance: AuraDef = {
  id: BBA.guardStance,
  name: 'Guard Stance',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  mods: [
    { stat: 'threatPct', value: 5 },
    { stat: 'damageTakenPct', value: -0.35 },
  ],
};
const reforming: AuraDef = { id: BBA.reforming, name: 'Spirit Re-forming', kind: 'debuff', durationMs: 8000, stacking: 'refresh', flags: { removeOutOfCombat: true } };
const mergeBuff: AuraDef = { id: BBA.merge, name: 'Spirit Merge', kind: 'buff', durationMs: 12_000, stacking: 'refresh', mods: [{ stat: 'hastePct', value: 0.3 }], ui: { proc: true, glow: { abilityIds: [BB.markPrey] } } };
const pinned: AuraDef = { id: BBA.pinned, name: 'Pinned', kind: 'debuff', durationMs: 3000, stacking: 'refresh', cc: 'root' };
const maulBleed: AuraDef = { id: BBA.maulBleed, name: 'Mauled', kind: 'debuff', durationMs: 8000, stacking: 'refresh', periodic: { intervalMs: 2000, effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.35 } }] } };
const packSprint: AuraDef = { id: BBA.sprint, name: 'Pack Stride', kind: 'buff', durationMs: 4000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: 0.4 }] };
const packStrideSpent: AuraDef = { id: BBA.strideSpent, name: 'Pack Stride', kind: 'debuff', durationMs: 30_000, stacking: 'refresh', flags: { hidden: true } };

/** The upkeep: the beast forms at your side, and re-forms after it falls (forms by level). */
const summonBeast: EffectSpec = {
  type: 'summon',
  npcId: BEAST_FORM.fox,
  count: 1,
  radius: 2,
  anchor: 'caster',
  companion: {
    ensure: true,
    forms: [
      { minLevel: BEAST_FORM_LEVEL.wolf, npcId: BEAST_FORM.wolf },
      { minLevel: BEAST_FORM_LEVEL.stag, npcId: BEAST_FORM.stag },
    ],
  },
};

/** Proc (Mark Prey, 25% with bad-luck protection): the next Pack Fury costs half the Bond. */
const kindred: AuraDef = {
  id: BBA.kindred,
  name: 'Kindred Spirit',
  kind: 'buff',
  durationMs: 15_000,
  stacking: 'refresh',
  mods: [{ stat: 'resourceCostPct', value: -0.5, abilityIds: [BB.packFury] }],
  procs: [{ on: 'castResolved', abilityIds: [BB.packFury], consumeStack: true, effects: [] }],
  ui: { proc: true, glow: { abilityIds: [BB.packFury] } },
};

const spiritBond: AuraDef = {
  id: BBA.bond,
  name: 'Spirit Bond',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  onApply: [summonBeast],
  periodic: { intervalMs: 1000, effects: [summonBeast] },
  procs: [{ on: 'castResolved', abilityIds: [BB.markPrey], chance: 0.35, badLuck: { step: 0.08 }, effects: [{ type: 'applyAura', to: 'self', aura: kindred }] }],
};

/** A beast move (cast through companionCast): its own damage, threat and Bond sync. */
const beastMove = (id: string, name: string, description: string, animAs: string, effects: EffectSpec[], targeting: AbilityDef['targeting'] = 'enemy'): AbilityDef => ({
  id,
  name,
  school: 'physical',
  description,
  targeting,
  melee: targeting === 'enemy',
  gcdMs: OFF_GCD,
  effects,
  ui: { animAs, color: '#ffd28a', desc: description },
});

const pounce = beastMove(BB.pounce, 'Leaping Bite', 'Your beast leaps on the marked target and bites.', RA.trueDraw, [{ type: 'damage', school: 'physical', amount: { coef: 0.75, variance: 0.05, perStack: { auraId: BBA.marked, on: 'target', mult: 0.3, fromSelf: false } } }]);
const furyBite = beastMove(BB.furyBite, 'Pack Fury Bites', 'Your beast tears into the target four times beside you.', RA.longshot, [0, 1, 2, 3].map(() => ({ type: 'damage', school: 'physical', amount: { coef: 0.42, variance: 0.08, perStack: { auraId: BBA.marked, on: 'target', mult: 0.3, fromSelf: false } } }) as EffectSpec));
const spiritPin = beastMove(BB.spiritPin, 'Spirit Pin', 'Your beast pins the target to the ground.', RA.snareCoil, [{ type: 'damage', school: 'physical', amount: { coef: 1.4, variance: 0.05 } }, { type: 'applyAura', aura: pinned }]);
const interpose = beastMove(
  BB.interpose,
  'Interpose',
  'Your beast bounds to where you stood and draws every enemy around it.',
  RA.backflip,
  [{ type: 'taunt', to: 'area', area: { anchor: 'point', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 6 }, durationMs: 3000 }],
  'none',
);
const maulBite = beastMove(BB.maulBite, 'Fanged Maul', 'Your beast mauls the target: a heavy bite and a bleeding wound.', RA.longshot, [{ type: 'damage', school: 'physical', amount: { coef: 2.2, variance: 0.05, perStack: { auraId: BBA.marked, on: 'target', mult: 0.3, fromSelf: false } } }, { type: 'applyAura', aura: maulBleed }]);
const growl = beastMove(BB.growl, 'Bristle', 'Your guarding beast snarls at what attacks you: it turns on the beast.', RA.pinShot, [{ type: 'taunt', durationMs: 3000 }]);
const howl = beastMove(BB.howl, 'Spirit Howl', 'Your beast howls with you.', RA.pinShot, [], 'none');
const spiritRend = beastMove(BB.spiritRend, 'Spirit Rend', 'Merged with you, your beast rakes everything around its prey.', RA.arrowfall, [{ type: 'damage', school: 'physical', to: 'area', area: aroundTarget(8, 6), amount: { coef: 0.9, variance: 0.05 }, isAoE: true }]);

/** The beast forms: fox (the spec, level 10), wolf (Rank II, 20), great stag (Ascension, 30). */
const beastNpc = (id: string, name: string, radius: number, powerPct: number, hpPct: number, color: string): NpcDef => ({
  id,
  name,
  tier: 'standard',
  hpMult: 1,
  forces: 0,
  abilities: [],
  radius,
  combatReach: 1.5,
  moveSpeed: 8,
  baseStats: { armor: 0.3, dodge: 0.05 },
  color,
  companion: {
    powerPct,
    hpPct,
    melee: { damage: { coef: 1.15, variance: 0.05, perStack: { auraId: BBA.marked, on: 'target', mult: 0.3, fromSelf: false } }, speedMs: 2000 },
    sync: { resource: BOND, amount: 5, windowMs: 1000, excludeAbilityIds: [BB.packFury, BB.furyBite] },
    guardAuraId: BBA.guarding,
    guardAura: guardStance,
    guardAbilityId: BB.growl,
    guardEveryMs: 5000,
    regenPct: 0.06,
    speedMult: 1.15,
    reformAuraId: BBA.reforming,
    reformMs: 8000,
  },
});

/** The three forms (registered in the content bundle's NPCs). */
export const BEAST_NPCS: NpcDef[] = [
  beastNpc(BEAST_FORM.fox, 'Spirit Fox', 0.7, 1.0, 0.6, '#ffb45a'),
  beastNpc(BEAST_FORM.wolf, 'Spirit Wolf', 0.9, 1.1, 0.7, '#9fd0ff'),
  beastNpc(BEAST_FORM.stag, 'Great Stag', 1.2, 1.2, 0.8, '#e8f5c8'),
];

const markPrey: AbilityDef = {
  id: BB.markPrey,
  name: 'Mark Prey',
  school: 'physical',
  description: 'Shoot and mark the target: your beast pounces on it, and hits Marked Prey 30% harder.',
  ...enemyShot,
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 2.9, variance: 0.05 } },
    { type: 'applyAura', aura: marked },
    { type: 'companionCast', abilityId: BB.pounce, leap: true },
    // Merged: the beast also rakes everything around its prey.
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: BBA.merge }, then: [{ type: 'companionCast', abilityId: BB.spiritRend }] },
  ],
  ui: {
    tags: ['builder'],
    color: '#e0a860',
    animAs: RA.trueDraw,
    desc: 'Shoot the target for {damage} Physical damage and mark it for 12 sec: your beast pounces on it right away, and hits Marked Prey 30% harder. Your hits and your beast’s on the same target within a second build Bond. 35% chance of Kindred Spirit: your next Pack Fury costs half the Bond.',
    hint: 'Press 1: you shoot, your beast pounces. Hitting together builds Bond.',
  },
};

const packFury: AbilityDef = {
  id: BB.packFury,
  name: 'Pack Fury',
  school: 'physical',
  description: 'Spend 50 Bond: you and your beast strike the target four times together.',
  ...enemyShot,
  cost: [{ resource: BOND, amount: 50 }],
  effects: [
    ...[0, 1, 2, 3].map(() => ({ type: 'damage', school: 'physical', amount: { coef: 1.15, variance: 0.08 } }) as EffectSpec),
    { type: 'companionCast', abilityId: BB.furyBite, leap: true },
  ],
  ui: {
    tags: ['spender'],
    color: '#ffb347',
    animAs: RA.longshot,
    desc: 'Spend 50 Bond: you fire four times into the target for {damage} Physical damage each while your beast tears into it four times beside you.',
    hint: 'Press 2 at 50 Bond: four shots and four bites at once.',
  },
};

const spiritSnare: AbilityDef = {
  id: BB.spiritSnare,
  name: 'Spirit Snare',
  school: 'nature',
  description: 'Send your beast to pin the target: it bites and roots it for 3 s. Builds 10 Bond.',
  ...enemyShot,
  cooldown: { durationMs: 20_000 },
  gain: [{ resource: BOND, amount: 10 }],
  effects: [{ type: 'companionCast', abilityId: BB.spiritPin, leap: true }],
  ui: { tags: ['cooldown'], color: '#c9a86a', animAs: RA.snareCoil, desc: 'Send your beast to pin the target: a heavy bite and a 3 sec root. Builds 10 Bond. 20 sec cooldown.', hint: 'Press 3 on cooldown: your beast pins the target and you gain Bond.' },
};

const boundLeap: AbilityDef = {
  id: BB.boundLeap,
  name: 'Bound Leap',
  school: 'nature',
  description: 'Leap 15 yd back; your beast bounds to where you stood and draws every enemy there onto itself.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  requiresMobility: true,
  usableWhileCasting: true,
  effects: [{ type: 'companionCast', abilityId: BB.interpose, toOwner: true }, { type: 'dash', mode: 'backward', distance: 15, speed: 36 }],
  ui: { tags: ['defensive', 'movement'], color: '#b5c99a', animAs: RA.backflip, desc: 'Leap 15 yd back. Your beast bounds to where you stood and taunts every enemy within 8 yd of it for 3 sec. Off the global cooldown.', hint: 'Press 4 when something reaches you: you leap away, your beast takes the hits.' },
};

const spiritHerd: AbilityDef = {
  id: BB.spiritHerd,
  name: 'Spirit Herd',
  school: 'nature',
  description: 'A herd of spirit beasts stampedes from you through the target: everything in a 40 yd line is trampled.',
  ...enemyShot,
  cooldown: { durationMs: 10_000 },
  effects: [{ type: 'damage', school: 'nature', to: 'area', area: { anchor: 'caster', shape: { type: 'line', length: 42, width: 6 }, affects: 'enemies', maxTargets: 10 }, amount: { coef: 3.0, variance: 0.05 }, isAoE: true }],
  ui: { tags: ['aoe', 'cooldown'], color: '#ffd28a', animAs: RA.arrowfall, desc: 'A herd of spirit beasts charges from you through the target: every enemy in a 40 yd line takes {damage} Nature damage. 10 sec cooldown.', hint: 'Press 5 on cooldown, best through a lined-up pack.' },
};

const packStride: AbilityDef = {
  id: BB.packStride,
  name: 'Pack Stride',
  school: 'nature',
  description: 'Out of combat: you (and your beast) run 30% faster (toggle). In combat: a 4 s sprint, and Fanged Maul is ready again (30 s).',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 1000 },
  usableIf: { type: 'or', any: [OUT_OF_COMBAT, { type: 'selfHasAura', auraId: BASE_AURA.swiftfoot }, { type: 'selfLacksAura', auraId: BBA.strideSpent }] },
  effects: [
    {
      type: 'conditional',
      cond: { type: 'selfInCombat' },
      then: [{ type: 'applyAura', to: 'self', aura: packSprint }, { type: 'modifyCooldown', abilityId: BB.maul, reset: true }, { type: 'applyAura', to: 'self', aura: packStrideSpent }],
      else: [swiftfootToggle],
    },
  ],
  ui: { tags: ['movement'], color: '#a8d672', animAs: RA.swiftfoot, desc: 'Out of combat: toggle, you and your beast run 30% faster until you press it again or a fight starts. In combat: sprint (+40% for 4 sec) and Fanged Maul is ready again. Once every 30 sec in combat.', hint: 'Press 6 to run between fights, or mid-fight for a second Fanged Maul.' },
};

const howlShot: AbilityDef = {
  id: BB.howlShot,
  name: 'Howl Shot',
  school: 'physical',
  description: "Shoot the caster's hand while your beast howls: interrupts its spell and locks that school for 3 s. Builds 10 Bond.",
  ...enemyShot,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15_000 },
  gain: [{ resource: BOND, amount: 10 }],
  effects: [{ type: 'damage', school: 'physical', amount: { coef: 0.6 } }, { type: 'interrupt', lockoutMs: 3000 }, { type: 'companionCast', abilityId: BB.howl }],
  ui: { tags: ['interrupt'], color: '#e0b25a', animAs: RA.pinShot, desc: "Shoot the caster's hand for {damage} Physical damage while your beast howls: interrupts its spell and locks that school for 3 sec. Builds 10 Bond. Off the global cooldown.", hint: 'Press 7 while an enemy casts.' },
};

const spiritMerge: AbilityDef = {
  id: BB.spiritMerge,
  name: 'Spirit Merge',
  school: 'nature',
  description: 'Fuse your spirit with the beast for 12 s: +30% haste for you both, and every Mark Prey also rakes everything around the prey.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60_000 },
  gain: [{ resource: BOND, amount: 25 }],
  effects: [{ type: 'applyAura', to: 'self', aura: mergeBuff }],
  ui: { tags: ['cooldown'], color: '#ffcf70', animAs: L.dawnbringer, desc: 'Fuse your spirit with your beast for 12 sec: +30% haste for you both, +25 Bond now, and every Mark Prey also has your beast rake every enemy within 8 yd of the prey. Off the global cooldown, 60 sec cooldown.', hint: 'Press Q for a burst: faster everything, and Mark Prey cleaves.' },
};

const guardCmd: AbilityDef = {
  id: BB.guard,
  name: 'Stand Guard',
  school: 'nature',
  description: 'Command: your beast stays at your side and protects you (taunts what attacks you, takes 35% less damage, ×6 threat). Press again to Hunt.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 1000 },
  effects: [{ type: 'applyAura', to: 'self', aura: guarding }],
  ui: { tags: ['defensive'], color: '#9fd0ff', animAs: AC.aegis, desc: 'Command your beast to Stand Guard: it stays at your side, turns whatever attacks you onto itself (a growl every 5 sec), takes 35% less damage and draws six times the threat. The button then becomes Hunt. Off the global cooldown.', hint: 'Press E when you are alone and getting hit: your beast tanks.' },
};

const huntCmd: AbilityDef = {
  id: BB.hunt,
  name: 'Hunt',
  school: 'nature',
  description: 'Command: your beast goes after whatever you attack. Press again to Stand Guard.',
  targeting: 'none',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 1000 },
  effects: [{ type: 'removeAura', to: 'self', auraId: BBA.guarding }],
  ui: { tags: ['cooldown'], color: '#ffb45a', animAs: RA.trueDraw, desc: 'Command your beast to Hunt: it attacks whatever you attack, at normal threat (a tank keeps the aggro). The button then becomes Stand Guard. Off the global cooldown.', hint: 'Press E in a group (a tank holds aggro): your beast hunts with you.' },
};

const maul: AbilityDef = {
  id: BB.maul,
  name: 'Fanged Maul',
  school: 'physical',
  description: 'Command: your beast mauls the target (Fanged Maul), a heavy bite and a bleed. Builds 8 Bond.',
  ...enemyShot,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 10_000 },
  gain: [{ resource: BOND, amount: 8 }],
  effects: [{ type: 'companionCast', abilityId: BB.maulBite, leap: true }],
  ui: { tags: ['cooldown'], color: '#ff9a5a', animAs: RA.longshot, desc: 'Command your beast to maul the target: a heavy bite and a bleed for 8 sec (more on Marked Prey). Builds 8 Bond. Off the global cooldown, 10 sec cooldown.', hint: 'Press R on cooldown: your beast’s big bite.' },
};

export const beastbound: SpecDef = {
  id: RANGER_SPEC.beastbound,
  baseClassId: 'ranger',
  name: RANGER_SPEC_NAME.beastbound,
  role: 'dps',
  unlockLevel: 10,
  evolutions: {
    [RA.trueDraw]: BB.markPrey,
    [RA.longshot]: BB.packFury,
    [RA.snareCoil]: BB.spiritSnare,
    [RA.backflip]: BB.boundLeap,
    [RA.arrowfall]: BB.spiritHerd,
    [RA.swiftfoot]: BB.packStride,
    [RA.pinShot]: BB.howlShot,
  },
  extraAbilities: [
    { key: 'Q', abilityId: BB.spiritMerge },
    { key: 'E', abilityId: BB.guard },
    { key: 'R', abilityId: BB.maul },
  ],
  abilities: [markPrey, packFury, spiritSnare, boundLeap, spiritHerd, packStride, howlShot, spiritMerge, guardCmd, huntCmd, maul, pounce, furyBite, spiritPin, interpose, maulBite, growl, howl, spiritRend],
  // Bond is the bar (0–100): synchronised hits build it, Pack Fury spends it; it fades out of combat.
  resources: [{ id: BOND, name: 'Bond', max: 100, start: 0, regenPerSecOoc: -4 }],
  passives: [spiritBond],
  auras: [marked, guarding, guardStance, reforming, mergeBuff, pinned, maulBleed, packSprint, packStrideSpent, kindred],
  color: '#d08b3c',
  ui: {
    tagline: 'Bonded to a spirit beast that evolves as you level.',
    fantasy: 'A spirit fox fights at your side: a wolf at Rank II, a great stag at Ascension. You fight as one, swapping its commands mid-pull: send it to hunt, call it to guard you, order the maul.',
    signature: 'Bond (0–100): your hits and your beast’s on the same target within a second build Bond; Pack Fury spends 50 for four strikes together. The beast has its own bar: Hunt / Stand Guard (E) and Fanged Maul (R).',
    weapon: 'bow',
    tint: '#d08b3c',
    glow: '#ffd28a',
  },
};

// ============================================================================
// Registry
// ============================================================================

/** Implemented Ranger specs. */
export const RANGER_SPECS: SpecDef[] = [deadeye, grovewarden, beastbound];

/** Preview info for all three (the launch set is bold in advancement.md). */
export const RANGER_SPEC_INFO: SpecInfo[] = [infoOf(deadeye, true), infoOf(grovewarden, true), infoOf(beastbound, false)];
